import { useSyncExternalStore } from "react"
import { useUserFlags } from "@/hooks/use-user-flags"

const STORAGE_KEY = "admin-override-enabled"

const listeners = new Set<() => void>()

const subscribe = (cb: () => void): (() => void) => {
  listeners.add(cb)
  const onStorage = (e: StorageEvent) => {
    if (e.key === STORAGE_KEY) cb()
  }
  window.addEventListener("storage", onStorage)
  return () => {
    listeners.delete(cb)
    window.removeEventListener("storage", onStorage)
  }
}

const getSnapshot = (): boolean => {
  if (typeof window === "undefined") return true
  const v = window.localStorage.getItem(STORAGE_KEY)
  return v === null ? true : v === "true"
}

const getServerSnapshot = (): boolean => true

export const setAdminLocal = (value: boolean): void => {
  if (typeof window === "undefined") return
  window.localStorage.setItem(STORAGE_KEY, String(value))
  listeners.forEach((cb) => cb())
}

export function useAdminLocal(): boolean {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot)
}

/**
 * Effective admin state for the UI. True only when the server flagged the
 * user as admin AND the user hasn't locally disabled the override in this
 * browser. Server can revoke, user can locally hide — never the other way.
 */
export function useIsAdmin(): boolean {
  const flags = useUserFlags()
  const local = useAdminLocal()
  return Boolean(flags?.isAdmin) && local
}

/** True when the server flagged the user as admin, ignoring the local
 *  override. Use this to decide whether to even show the admin override
 *  switch. */
export function useServerIsAdmin(): boolean {
  const flags = useUserFlags()
  return Boolean(flags?.isAdmin)
}
